import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Grid,
  Paper
} from '@mui/material';
import {
  CloudUpload,
  Delete
} from '@mui/icons-material';
import { productService } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';

const AddProductModal = ({ open, onClose, onSuccess }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    dosage: '',
    quantity: '',
    unit: '',
    priceNigeria: '',
    isAvailable: true
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    'prescription',
    'over-the-counter',
    'supplements',
    'medical-devices',
    'personal-care'
  ];

  const units = [
    'tablet',
    'capsule',
    'ml',
    'mg',
    'g',
    'piece',
    'bottle',
    'tube'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Please select only image files (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Limit to 5 images
    if (images.length + files.length > 5) {
      setError('You can upload maximum 5 images');
      return;
    }

    // Validate file sizes (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setError('Image files must be smaller than 5MB');
      return;
    }

    setImages(prev => [...prev, ...files]);
    
    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreviews(prev => [...prev, {
          file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });

    // Clear error if successful
    if (error) setError(null);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Debug authentication
      console.log('Auth Debug:', { user, token, isAuthenticated, userType: user?.userType });
      
      if (!isAuthenticated) {
        throw new Error('You must be logged in to add products');
      }

      if (user?.userType !== 'pharmacy') {
        throw new Error('Only pharmacy users can add products');
      }

      // Validate required fields
      if (!formData.name || !formData.category || !formData.quantity || !formData.unit || !formData.priceNigeria) {
        throw new Error('Please fill in all required fields');
      }

      if (isNaN(formData.quantity) || parseFloat(formData.quantity) <= 0) {
        throw new Error('Please enter a valid quantity');
      }

      if (isNaN(formData.priceNigeria) || parseFloat(formData.priceNigeria) <= 0) {
        throw new Error('Please enter a valid price');
      }

      const productData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        brand: formData.brand,
        dosage: formData.dosage,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        'price.nigeria': parseFloat(formData.priceNigeria),
        'price.international': parseFloat(formData.priceNigeria) * 250, // Convert to USD equivalent
        isAvailable: formData.isAvailable,
        images: images // Add images to the data
      };

      await productService.addProduct(productData);
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        category: '',
        brand: '',
        dosage: '',
        quantity: '',
        unit: '',
        priceNigeria: '',
        isAvailable: true
      });
      setImages([]);
      setImagePreviews([]);
    } catch (error) {
      console.error('Error adding product:', error);
      setError(error.response?.data?.message || error.message || 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      name: '',
      description: '',
      category: '',
      brand: '',
      dosage: '',
      quantity: '',
      unit: '',
      priceNigeria: '',
      isAvailable: true
    });
    setImages([]);
    setImagePreviews([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Product</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Product Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            autoFocus
          />
          
          <TextField
            margin="normal"
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
          />
          
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
              label="Category"
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            fullWidth
            label="Brand"
            name="brand"
            value={formData.brand}
            onChange={handleChange}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Dosage"
            name="dosage"
            value={formData.dosage}
            onChange={handleChange}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Quantity"
            name="quantity"
            type="number"
            value={formData.quantity}
            onChange={handleChange}
            inputProps={{ min: 1 }}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Unit</InputLabel>
            <Select
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              label="Unit"
            >
              {units.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Price (₦)"
            name="priceNigeria"
            type="number"
            value={formData.priceNigeria}
            onChange={handleChange}
            inputProps={{ min: 0, step: 0.01 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.isAvailable}
                onChange={handleChange}
                name="isAvailable"
              />
            }
            label="Available for Sale"
            sx={{ mt: 2 }}
          />

          {/* Image Upload Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Product Images
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload up to 5 images (JPEG, PNG, GIF, WebP - max 5MB each)
            </Typography>
            
            {/* Upload Button */}
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="image-upload"
              type="file"
              multiple
              onChange={handleImageChange}
            />
            <label htmlFor="image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                sx={{ mb: 2 }}
                disabled={images.length >= 5}
              >
                Upload Images
              </Button>
            </label>

            {/* Image Previews */}
            {imagePreviews.length > 0 && (
              <Grid container spacing={2}>
                {imagePreviews.map((image, index) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                    <Paper
                      elevation={2}
                      sx={{
                        position: 'relative',
                        padding: 1,
                        textAlign: 'center'
                      }}
                    >
                      <Box
                        component="img"
                        src={image.preview}
                        alt={image.name}
                        sx={{
                          width: '100%',
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1
                        }}
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mt: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {image.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)'
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Add Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductModal;
